import { motion } from "framer-motion";

/**
 * AnimatedIcon — premium animated icon container.
 * - Gentle infinite float + glow pulse
 * - On-view scale-in / rotate-in
 * - Hover: scale + glow burst
 * - Optional ring badge with monospaced index
 */
export default function AnimatedIcon({
  Icon,
  index,
  size = "h-12 w-12",
  iconClass = "h-5 w-5",
  tone = "ocean", // "ocean" | "chrome"
  testId,
  className = "",
}) {
  const glow =
    tone === "ocean"
      ? "rgba(0,229,255,0.35)"
      : "rgba(255,255,255,0.18)";
  const grad =
    tone === "ocean"
      ? "bg-[radial-gradient(circle_at_30%_30%,rgba(0,229,255,0.35),transparent_70%)]"
      : "bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.12),transparent_70%)]";
  const stroke = tone === "ocean" ? "text-[#00E5FF]" : "text-white/90";

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
      whileHover={{ scale: 1.08, rotate: 2 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        type: "spring",
        stiffness: 220,
        damping: 18,
        mass: 0.7,
      }}
      className={`relative inline-flex items-center justify-center ${size} rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden group ${className}`}
      style={{
        boxShadow: `0 0 0 0 ${glow}`,
      }}
    >
      {/* Pulsing radial backdrop */}
      <motion.div
        className={`absolute inset-0 ${grad}`}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Soft outer ring sweep */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 rounded-2xl"
        style={{
          background:
            "conic-gradient(from 0deg, rgba(0,229,255,0) 0deg, rgba(0,229,255,0.4) 60deg, rgba(0,229,255,0) 120deg, rgba(0,85,255,0) 360deg)",
          mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      {/* Floating icon */}
      <motion.span
        className={`relative ${stroke}`}
        animate={{ y: [0, -2, 0] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <Icon className={iconClass} strokeWidth={1.5} />
      </motion.span>

      {/* Index badge */}
      {index != null && (
        <span className="absolute top-1.5 right-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
          {index}
        </span>
      )}

      {/* Hover glow burst */}
      <motion.div
        className="absolute -inset-4 rounded-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,85,255,0.35), transparent 60%)",
        }}
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      />
    </motion.div>
  );
}
